<script>
    /*
	    ReviewPage.svelte

        This page will show either:

            <ReviewFetchPanel>
            <ReviewList>
            <ReviewEditPanel>

        It will be in either LIST_MODE or EDIT_MODE

        08/02 - left side for MERGE type will contain array of services[]
        08/22 - now using masterReview object for API calls to server
        08/25 - fetchCount and fetchType are now here in parent - not in FetchPanel
        
    */

    import { onMount } from 'svelte';
    import { get } from 'svelte/store';
    import { fly, fade } from 'svelte/transition';
    import { push } from 'svelte-spa-router';
    import {approveReviews2,  fetchReviews2, rejectReviews2} from '../../api/services.ts';

    import { hasSessionRoles } from '../../common/auth.ts';
    import { setInfoAlert, setErrorAlert, PENDING_MSECS } from '../../stores/AlertsStore';
    import { masterReview } from '../../common/master-objects.js';
    import { isReviewEdited } from '../../common/utils.js';
	import { fetchAllFieldsPresets } from '../../stores/FieldPresetsStore.js';

    import { FETCH_REVIEWS_ERROR } from '../../stores/AlertsStore';
    import { REVIEW_CREATE } from '../../common/constants.js';
    import { ROLE_REVIEW_WRITE } from '../../common/roles-actions.js';


    // TCARE components
    import PageBanner from '../../components/common/PageBanner.svelte';
    import CircularSpinner from '../../components/common/CircularSpinner.svelte';

    import ReviewList from '../../components/lists/ReviewList.svelte';
    import ReviewFetchPanel from '../../components/reviews/ReviewFetchPanel.svelte';
    import ReviewApproveRejectPanel from '../../components/reviews/ReviewApproveRejectPanel.svelte';
    import {AccountStore} from "../../common/account";

    // SMUI components

    export const LIST_MODE = "LIST_MODE";
    export const EDIT_MODE = "EDIT_MODE";

    const APPROVE_PENDING_MSG = 'Submitting your Approve requests - click to cancel';
    const APPROVE_ERROR_MSG = 'Error while submitting your Approve requests';
    const APPROVE_SUCCESS_MSG = 'Successfully submitted your Approve requests';
    const REJECT_PENDING_MSG = 'Submitting your Reject requests - click to cancel';
    const REJECT_ERROR_MSG = 'Error while submitting your Reject requests';
    const REJECT_SUCCESS_MSG = 'Successfully submitted your Reject requests';
    
    const REQ_ROLE = ROLE_REVIEW_WRITE;                // to visit this page
    const REQ_ROLE_ERROR_MSG = "We're sorry - you must have a '" + REQ_ROLE + "' role to visit the Reviews page";

    let selectedReview = null;

    // new 8/25 - pass and set these in the FetchPanel
    $: fetchMostRecent = false;
    $: fetchCount = 100;
    $: fetchType = REVIEW_CREATE;


    $: pageMode = LIST_MODE;
    $: reviews = [];                                    // returned from fetchReviews API
    $: isFetchingReviews = false;

    // restrict to user.idTokenClaims.roles -> review.write
    onMount(() => {

        if (!$AccountStore?.isLoggedIn) {
            push('/');                                  // and login
        }

		fetchAllFieldsPresets();
		console.log('ReviewPage fetching all fields presets');

        scrollTo(0, 0);
    })

    // passed to the FetchPanel
    const onSetFetchType = (type) => {
        fetchType = type;
    }

    // API calls

    // passed to FetchPanel - called when fetch button clicked
    // this is the API data returned with no review selected yet
    const onFetchReviews = async () => {
        // console.log('ReviewPage onFetchReviews: ', fetchCount, fetchType);
        isFetchingReviews = true;
        reviews = [];

        let resp = await fetchReviews2(fetchCount, fetchType, fetchMostRecent);
        if (resp.errors) {
            setErrorAlert(FETCH_REVIEWS_ERROR, 2000);
            reviews = [];
        } else {
            reviews = resp;
        }

        isFetchingReviews = false;
        // console.log(reviews);
    }

    // selected when the ThumbUpDown icon is clicked in the ReviewListItem 
    // MUST SET THE masterReview OBJECT HERE !!!
    const onEditReview = (review) => {
        // console.log('ReviewPage onEditReview - called from list item click');
        // console.log('review:');
        // console.log(review);
        selectedReview = review;                // cache it for later comparison
        masterReview.copyReview(review);
        pageMode = EDIT_MODE;
    }

    const onCloseReview = () => {
        //alert('ReviewPage onCloseReview');
        pageMode = LIST_MODE;
    }


    // 8/22 new API format per Vito
    /*
    {
        ids: ["123"], // review ids
        containsReviewerInput: true,
        finalServices: {  // map of review ids to service data
            "123": {
                id: 123131,
                serviceName: "abcco",
                // rest of fields
            }
        }
    }
    */

    // calls approveReviews()
    const onApproveReview = async () => {
        // console.log('ReviewPage onApproveReview - single');
        const oldData = selectedReview.rightService;            // from list item
        const newData = get(masterReview.rightServiceObj).apiData;
        // CAUTION - do not compare left and right - compare right old with right new
        let isEdited = isReviewEdited(oldData, newData);
        let body = masterReview.formatReviewApiBody(isEdited);

        // CALL API HERE!
        // passing in the function - will not run if Alert is cancelled
        setInfoAlert(APPROVE_PENDING_MSG, PENDING_MSECS, async () => {
            let json = await approveReviews2(body);
            if (!json.errors) {
                setInfoAlert(APPROVE_SUCCESS_MSG, 2000);
                onFetchReviews();                               // and stay on same list
                //pop();
            } else {
                setErrorAlert(APPROVE_ERROR_MSG, 2000);
            }
        });
    }
    
    // just an array of the rejected ids?
    // calls rejectReviews()
    const onRejectReview = async () => {
        // console.log('ReviewPage onRejectReview - single');
        let body = masterReview.formatReviewApiBody(false);

        // CALL API HERE!
        // passing in the function - will not run if Alert is cancelled
        setInfoAlert(REJECT_PENDING_MSG, PENDING_MSECS, async () => {
            let json = await rejectReviews2(body);
            if (!json.errors) {
                setInfoAlert(REJECT_SUCCESS_MSG, 2000);
                onFetchReviews();                               // and stay on same list
                //pop();
            } else {
                setErrorAlert(REJECT_ERROR_MSG, 2000);
            }
        });
    }
    
</script>

<style>
    :global(.count-select) {
        width: 100px;
        margin-left: 25px;
    }

    .fetch-spinner {
        width: 300px;
        margin: auto;
        margin-top: 20px;
    }

    @media screen and (max-width: 600px) {
    }

    @media screen and (min-width: 601px) and (max-width: 1300px) {
    }

    @media screen and (min-width: 1201px) {
    }

</style>

<div class="page-content review-page" in:fly="{{ x: 200, duration: 500 }}" out:fade="{{ duration: 250 }}">
    <div class="spacer-h-25"></div>
    <PageBanner title="Manage Reviews"/>
    <div class="spacer-h-25"></div>


        {#if pageMode === LIST_MODE}
            <ReviewFetchPanel
                bind:fetchCount
                bind:fetchType
                bind:fetchMostRecent
                {onFetchReviews}
            />
            <div class="spacer-h-10"></div>
            {#if reviews.length > 0}
                <ReviewList items={reviews} {onEditReview} />
            {:else}
                 <p> No reviews. </p>
            {/if}
        {/if}
        {#if (pageMode === EDIT_MODE)}
            <ReviewApproveRejectPanel 
                review={masterReview} 
                {onApproveReview}
                {onRejectReview}
                {onCloseReview} />
        {/if}

    <div class="spacer-h-50"></div>
</div>
